import { useEffect, useState } from "react";
import { ShieldCheck, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Non-affiliation disclaimer + the government sources the engine computes
 * from. Text comes from the backend so the app, the PDFs and the store
 * listing can never disagree. Rendered in Company Settings and, compactly,
 * in the landing footer.
 */
export default function ComplianceDisclosures({ compact = false }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    fetch(`${API}/compliance/disclosures`)
      .then((r) => r.json())
      .then(setD)
      .catch(() => setD(null));
  }, []);

  if (!d) return null;

  if (compact) {
    return (
      <div className="font-mono text-[11px] leading-relaxed" data-testid="compliance-footer">
        <div className="text-white/70 max-w-3xl" data-testid="compliance-footer-disclaimer">{d.disclaimer}</div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {d.sources.slice(0, 2).map((s, i) => (
            <a key={`${s.url}-${i}`} href={s.url} target="_blank" rel="noreferrer"
               data-testid={`compliance-footer-link-${s.url.includes("ssa.gov") ? "ssa" : "irs"}`}
               className="underline hover:text-white">{s.name.split("—")[0].trim()}</a>
          ))}
          <a href={`${API}/compliance/disclosures`} target="_blank" rel="noreferrer"
             data-testid="compliance-footer-all-sources" className="underline hover:text-white">
            All calculation sources
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="hard-card p-6 mt-8" data-testid="compliance-section">
      <div className="overline mb-3 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> § COMPLIANCE &amp; DISCLAIMERS
      </div>
      <p className="font-mono text-xs leading-relaxed max-w-3xl mb-2" data-testid="compliance-disclaimer">
        {d.disclaimer}
      </p>
      <p className="font-mono text-[11px] text-muted-foreground max-w-3xl mb-5">
        {d.tagline} Checkmate does not transmit anything to the IRS, the SSA or a state agency
        on your behalf — you upload the file and keep the agency's confirmation. The same notice
        appears in the footer of every PDF this system generates, including pay stubs.
      </p>
      <div className="overline mb-3">Calculation sources (public government documentation)</div>
      <ul className="space-y-3" data-testid="compliance-sources">
        {d.sources.map((s, i) => (
          <li key={`${s.url}-${i}`} className="border-l-2 border-ink pl-3">
            <a href={s.url} target="_blank" rel="noreferrer"
               data-testid={`compliance-source-${s.url.replace(/[^a-z0-9]/gi, "-").slice(0, 40)}`}
               className="font-mono text-xs font-black underline inline-flex items-start gap-1 hover:text-accent">
              {s.name} <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />
            </a>
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{s.used_for}</div>
            <div className="font-mono text-[10px] text-muted-foreground break-all">{s.url}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
